// Iteration 49 — Admin Tech Guide Feedback dashboard.
// Aggregated ratings by cache_key (op_key + vehicle) with negative-first ordering.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Loader2, RefreshCw, MessageSquare, TrendingDown } from "lucide-react";

export default function AdminTechGuideFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("");
  const [onlyNegative, setOnlyNegative] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { only_negative: onlyNegative };
      if (kind) params.kind = kind;
      const r = await api.get("/tech-guide/admin/feedback", { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, onlyNegative]);

  const totals = items.reduce(
    (a, it) => ({
      up: a.up + (it.up_count || 0),
      down: a.down + (it.down_count || 0),
      guides: a.guides + 1,
    }),
    { up: 0, down: 0, guides: 0 },
  );

  return (
    <div className="space-y-6 pb-16" data-testid="admin-tech-guide-feedback">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingDown className="w-6 h-6 text-cyan-400" /> Tech Guide Feedback
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Ratings from technicians — negative-first so you spot bad guides fast.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm"
          data-testid="feedback-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={ThumbsUp} label="Thumbs up" value={totals.up} color="emerald" />
        <StatCard icon={ThumbsDown} label="Thumbs down" value={totals.down} color="rose" />
        <StatCard icon={MessageSquare} label="Rated guides" value={totals.guides} color="cyan" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-wider text-slate-500">Kind</label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm"
          data-testid="feedback-kind-filter"
        >
          <option value="">All</option>
          <option value="op">Operations (ADAS)</option>
          <option value="dtc">DTC Guides</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyNegative}
            onChange={(e) => setOnlyNegative(e.target.checked)}
            data-testid="feedback-only-negative"
          />
          Only guides with 1+ thumbs down
        </label>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-center text-slate-500 py-16">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-500 border border-dashed border-slate-800 rounded-lg py-16">
          {onlyNegative ? "No negatively-rated guides yet 🎉" : "No feedback collected yet."}
        </div>
      ) : (
        <div className="border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Guide</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-center px-3 py-3">
                  <ThumbsUp className="w-3 h-3 inline text-emerald-400" />
                </th>
                <th className="text-center px-3 py-3">
                  <ThumbsDown className="w-3 h-3 inline text-rose-400" />
                </th>
                <th className="text-center px-3 py-3">Score</th>
                <th className="text-left px-4 py-3">Last</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const isOp = it.kind === "op";
                const isExpanded = !!expanded[it.cache_key];
                const label = it.op_name || it.op_key || it.dtc_key || "—";
                return (
                  <React.Fragment key={it.cache_key || idx}>
                    <tr
                      className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer"
                      onClick={() => setExpanded({ ...expanded, [it.cache_key]: !isExpanded })}
                      data-testid={`feedback-row-${idx}`}
                    >
                      <td className="px-4 py-3">
                        <Badge className={isOp ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-200 text-[9px] uppercase mr-2" : "bg-amber-500/20 border-amber-500/40 text-amber-200 text-[9px] uppercase mr-2"}>
                          {it.kind}
                        </Badge>
                        <span className="text-slate-200 font-medium">{label}</span>
                        {isOp && it.op_key && it.op_name !== it.op_key && (
                          <span className="text-slate-600 text-[10px] ml-1.5 font-mono">{it.op_key}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {it.vehicle_label || <span className="text-slate-700">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center text-emerald-300">{it.up_count || 0}</td>
                      <td className="px-3 py-3 text-center text-rose-300">{it.down_count || 0}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={
                          (it.score || 0) < 0
                            ? "text-rose-300 font-semibold"
                            : (it.score || 0) > 0 ? "text-emerald-300 font-semibold" : "text-slate-400"
                        }>
                          {it.score > 0 ? `+${it.score}` : it.score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-[11px]">
                        {it.last_rated_at ? new Date(it.last_rated_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-2 py-3 text-slate-600 text-xs">
                        {(it.comments?.length || 0) > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> {it.comments.length}
                          </span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (it.comments?.length || 0) > 0 && (
                      <tr className="bg-slate-950/60">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Comments</div>
                          <ul className="space-y-2">
                            {it.comments.map((c, ci) => (
                              <li key={ci} className="text-xs flex items-start gap-2">
                                {c.rating === "down"
                                  ? <ThumbsDown className="w-3 h-3 text-rose-400 mt-0.5 flex-none" />
                                  : <ThumbsUp className="w-3 h-3 text-emerald-400 mt-0.5 flex-none" />}
                                <div className="flex-1">
                                  <div className="text-slate-200">{c.comment}</div>
                                  <div className="text-[10px] text-slate-600">
                                    {c.user || "anonymous"} · {c.at ? new Date(c.at).toLocaleString() : ""}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const cls = {
    emerald: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    rose: "border-rose-500/30 text-rose-300 bg-rose-500/5",
    cyan: "border-cyan-500/30 text-cyan-300 bg-cyan-500/5",
  }[color] || "border-slate-800 text-slate-300";
  return (
    <div className={`rounded-lg border p-4 ${cls}`} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
